import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IconSearch } from './icons';
import { Tag } from './ui';

/** Opinion (-100..100) as a word, so the player never sees the raw number. */
export type Mood = 'friendly' | 'neutral' | 'wary' | 'hostile';
export function moodOf(opinion: number): Mood {
  return opinion >= 25 ? 'friendly' : opinion >= -10 ? 'neutral' : opinion >= -40 ? 'wary' : 'hostile';
}

export type Verdict = 'own' | 'likely' | 'possible' | 'unlikely' | 'full';
const VERDICT_TONE = { own: 'gold', likely: 'green', possible: 'blue', unlikely: 'red', full: 'red' } as const;

/** Coloured verdict chip; `label` is the already-translated text. */
export function VerdictChip({ verdict, label }: { verdict: Verdict; label: string }) {
  return <Tag tone={VERDICT_TONE[verdict]}>{label}</Tag>;
}

/** One search field plus a "Filters" button (with an active-filter count) that toggles a compact filter area. */
export function FilterBar({ search, onSearch, placeholder, activeCount, children }: {
  search: string; onSearch: (v: string) => void; placeholder: string; activeCount: number; children: ReactNode;
}) {
  const { t } = useTranslation();
  // Collapsed on phones; on wide screens the compact area fits in a row or two, so start open.
  const [open, setOpen] = useState(() => typeof window !== 'undefined' && window.matchMedia?.('(min-width: 900px)').matches === true);
  return (
    <div className="fbar">
      <div className="fbar-row">
        <label className="fbar-search">
          <IconSearch size={18} />
          <input className="input" type="search" maxLength={64} placeholder={placeholder} aria-label={placeholder} value={search} onChange={(e) => onSearch(e.target.value)} />
        </label>
        <button type="button" className={`fbar-btn ${open ? 'on' : ''}`} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><path d="M3 5h14M6 10h8M8.500 15h3" stroke="currentColor" strokeWidth="2.200" fill="none" strokeLinecap="round" /></svg>
          <span>{t('pick.filters')}</span>
          {activeCount > 0 && <span className="fbar-badge" aria-label={t('pick.active', { count: activeCount })}>{activeCount}</span>}
        </button>
      </div>
      {open && <div className="fbar-panel">{children}</div>}
    </div>
  );
}

/** Small wrapping chips, single choice. */
export function Chips<T extends string>({ value, options, onChange, label }: { value: T; options: { value: T; label: ReactNode }[]; onChange: (v: T) => void; label: string }) {
  return (
    <div className="chips" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={o.value === value} className={`chip ${o.value === value ? 'on' : ''}`} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

/** A compact selectable row: radio semantics on the main button, an optional profile link beside it. */
export function PickRow({ selected, disabled, onSelect, profileTo, profileLabel, className = '', children }: {
  selected: boolean; disabled?: boolean; onSelect: () => void; profileTo?: string; profileLabel?: string; className?: string; children: ReactNode;
}) {
  return (
    <div className={`pk-row ${selected ? 'sel' : ''} ${disabled ? 'disabled' : ''} ${className}`}>
      <button type="button" role="radio" aria-checked={selected} aria-disabled={disabled} disabled={disabled} className="pk-main" onClick={onSelect}>
        {children}
      </button>
      {profileTo && <Link to={profileTo} className="pk-profile" aria-label={profileLabel} title={profileLabel}>›</Link>}
    </div>
  );
}
